import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Typography } from "@mui/material";
import type { FC } from "react";

import type { GetUpdatesSource } from "~/feature/auth/type";
import { GetUpdatesFinePrint } from "~/feature/getUpdates/components/GetUpdatesFinePrint";

import * as Styled from "./GetUpdatesForm.styles";
import { useGetUpdatesForm } from "./useGetUpdatesForm";

const EMAIL_INPUT_ID = "get-updates-email";

interface IGetUpdatesFormProps {
  onClose: () => void;
  source: GetUpdatesSource;
  ink?: boolean;
}

export const GetUpdatesForm: FC<IGetUpdatesFormProps> = ({
  onClose,
  source,
  ink,
}) => {
  const { formik, submittedEmail, showRecaptchaError } =
    useGetUpdatesForm(source);

  if (submittedEmail) {
    return (
      <Styled.SuccessRoot>
        <Styled.SuccessIconTile>
          <CheckRoundedIcon />
        </Styled.SuccessIconTile>
        <Styled.SuccessTitle $ink={ink}>Briefing on its way</Styled.SuccessTitle>
        <Styled.SuccessBody $ink={ink}>
          Sent to {submittedEmail}. If it isn&apos;t there in a minute, check
          spam and add us to your contacts.
        </Styled.SuccessBody>
        <Styled.BackButton variant="outlined" $ink={ink} onClick={onClose}>
          Back to markets
        </Styled.BackButton>
      </Styled.SuccessRoot>
    );
  }

  return (
    <Styled.Form onSubmit={formik.handleSubmit} noValidate>
      <Styled.FieldLabel htmlFor={EMAIL_INPUT_ID} $ink={ink}>
        Email
      </Styled.FieldLabel>
      <Styled.EmailField
        id={EMAIL_INPUT_ID}
        $ink={ink}
        type="email"
        name="email"
        placeholder="you@email.com"
        value={formik.values.email}
        onChange={formik.handleChange}
        error={Boolean(formik.touched.email && formik.errors.email)}
        helperText={formik.touched.email ? formik.errors.email : undefined}
        autoComplete="email"
        fullWidth
        slotProps={{ input: { readOnly: formik.isSubmitting } }}
      />
      {showRecaptchaError ? (
        <Typography variant="caption" color="error">
          {formik.errors.recaptchaToken}
        </Typography>
      ) : null}
      <Styled.SubmitButton
        type="submit"
        variant="contained"
        loading={formik.isSubmitting}
        loadingPosition="start"
        fullWidth
        $ink={ink}
      >
        {formik.isSubmitting ? "Sending…" : "Access cross-market intelligence"}
      </Styled.SubmitButton>
      <Styled.Microcopy $ink={ink}>
        Free during early access. Twice weekly. Unsubscribe anytime.
      </Styled.Microcopy>
      <GetUpdatesFinePrint agreement="subscribing" ink={ink} />
    </Styled.Form>
  );
};
